import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Modal, useToast } from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  formatNodeMoney,
  humanizeCreditTransferError,
  settleFromPostedPnl,
  type HierarchyNode,
} from './hierarchy'

type Planned = {
  account: HierarchyNode
  direction: 'give' | 'take'
  amountMinor: string
  amountLabel: string
}

/**
 * Lab Bulk transfer confirm — show Give/Take amounts before moving money
 * (Agent-UI BulkSettleDialog plan step, simplified).
 */
export function BulkSettleDialog({
  selected,
  onClose,
}: {
  selected: HierarchyNode[]
  onClose: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)

  const plan = useMemo(() => {
    const rows: Planned[] = []
    for (const account of selected) {
      const move = settleFromPostedPnl(account.postedPnl, account.currencyType)
      if (!move) continue
      rows.push({
        account,
        direction: move.direction,
        amountMinor: move.amountMinor,
        amountLabel: formatNodeMoney(move.amountMinor, account.currencyType),
      })
    }
    // Takes first (raise parent room), then gives ascending — Backend-UI order.
    const takes = rows
      .filter((r) => r.direction === 'take')
      .sort((a, b) => Number(b.amountMinor) - Number(a.amountMinor))
    const gives = rows
      .filter((r) => r.direction === 'give')
      .sort((a, b) => Number(a.amountMinor) - Number(b.amountMinor))
    return [...takes, ...gives]
  }, [selected])

  const run = async () => {
    if (!plan.length) {
      toast.error('No Take / Give outstanding on the selection')
      return
    }
    setBusy(true)
    let ok = 0
    let failed = 0
    const errors: string[] = []
    try {
      for (const row of plan) {
        try {
          await cmApi.hierarchyCreditTransfer(
            authHeaders(),
            {
              id: row.account.id,
              direction: row.direction,
              amount: row.amountMinor,
            },
            crypto.randomUUID(),
            { bulkTransfer: true },
          )
          ok += 1
        } catch (err) {
          failed += 1
          const msg = humanizeCreditTransferError(
            err instanceof Error ? err.message : 'failed',
            row.account.currencyType,
          )
          errors.push(`${row.account.username || row.account.name}: ${msg}`)
        }
      }
      await qc.refetchQueries({ queryKey: ['cm', 'hierarchy'] })
      if (failed === 0) {
        toast.success(`Settled ${ok} account${ok === 1 ? '' : 's'}`)
        onClose()
      } else {
        toast.error(
          `Settled ${ok}, failed ${failed}`,
          errors.slice(0, 3).join(' · ') || undefined,
        )
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      title="Bulk transfer"
      subtitle="Settle Take / Give to zero on the selected direct children"
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!plan.length} onClick={() => void run()}>
            Confirm {plan.length} transfer{plan.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="cm-bulk-settle">
        {!plan.length ? (
          <Banner tone="warn" title="Nothing to settle">
            Selected accounts have no Take or Give outstanding. Refresh the list and try again.
          </Banner>
        ) : (
          <>
            <p className="cm-hint">
              Review the amounts below. Settlement Give/Take zeros Lab balanceUp — it does not
              require cash under the credit limit.
            </p>
            <table className="cm-bulk-settle-table">
              <thead>
                <tr>
                  <th scope="col">Account</th>
                  <th scope="col">Direction</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((row) => (
                  <tr key={row.account.id}>
                    <td>
                      <strong>{row.account.name}</strong>
                      {row.account.username ? (
                        <div className="cm-hier-meta">{row.account.username}</div>
                      ) : null}
                    </td>
                    <td className={row.direction === 'give' ? 'is-give' : 'is-take'}>
                      {row.direction === 'give' ? 'Give' : 'Take'}
                    </td>
                    <td>{row.amountLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Modal>
  )
}
